import { computed, ref } from 'vue';
import type { PushConfig, PushDevice } from '@its/shared';
import { api } from './api';

/**
 * LES NOTIFICATIONS PUSH, CÔTÉ ÉCRAN — être prévenu l'outil fermé.
 *
 * ===========================================================================
 * CE QUE ÇA COMBLE. Le temps réel (SSE) suppose un onglet ouvert ; le mail se
 * relève. Entre les deux, rien. Le push s'adresse à l'APPAREIL.
 *
 * UN ABONNEMENT PAR NAVIGATEUR, pas par compte : ce que le navigateur remet
 * est un tuyau vers cette installation-ci. Quelqu'un qui travaille sur son
 * poste et son téléphone active donc les deux, séparément — et l'écran liste
 * ce qui est actif ailleurs, pour qu'on sache d'où viennent les alertes.
 *
 * TROIS CHOSES PEUVENT MANQUER, et il faut les distinguer à l'écran, parce
 * qu'aucune ne se répare de la même façon :
 *
 *   - le NAVIGATEUR ne sait pas faire (Safari de bureau avant 16.4, iPhone où
 *     l'outil n'a pas été ajouté à l'écran d'accueil) ;
 *   - l'ENVIRONNEMENT n'a pas de clés VAPID — c'est le cas nominal en local et
 *     en intégration continue (règle 9), pas une panne ;
 *   - la PERMISSION a été refusée. Celle-là ne se redemande pas : le
 *     navigateur répond « non » sans rien afficher, et seul l'utilisateur peut
 *     revenir dessus dans les réglages du site. Lui proposer un bouton qui ne
 *     fait rien est le meilleur moyen de faire croire à une panne.
 * ===========================================================================
 */

export type EtatPush =
  /** Le navigateur n'a ni service worker, ni API de notification. */
  | 'indisponible'
  /** Pas de clés VAPID sur cet environnement. */
  | 'non_configure'
  /** Permission refusée — irréversible depuis l'application. */
  | 'refuse'
  /** Tout est prêt, mais cet appareil n'est pas abonné. */
  | 'inactif'
  /** Cet appareil reçoit les notifications. */
  | 'actif';

/** L'appareil abonné, mémorisé ici : c'est ce qui permet de se reconnaître dans la liste. */
const CLE_APPAREIL = 'pa.push.appareil';

const config = ref<PushConfig | null>(null);
const abonneIci = ref(false);
const permission = ref<NotificationPermission>('default');
const occupe = ref(false);

export const appareils = computed<PushDevice[]>(() => config.value?.devices ?? []);
export const appareilCourantId = computed<string | null>(() => lireId());
export const pushOccupe = computed(() => occupe.value);

/** Ce que le navigateur sait faire, indépendamment de l'environnement. */
function supporte(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export const etatPush = computed<EtatPush>(() => {
  if (!supporte()) return 'indisponible';
  if (!config.value?.available) return 'non_configure';
  if (permission.value === 'denied') return 'refuse';
  return abonneIci.value ? 'actif' : 'inactif';
});

function lireId(): string | null {
  try {
    return localStorage.getItem(CLE_APPAREIL);
  } catch {
    return null;
  }
}
function ecrireId(id: string | null): void {
  try {
    if (id) localStorage.setItem(CLE_APPAREIL, id);
    else localStorage.removeItem(CLE_APPAREIL);
  } catch {
    /* stockage indisponible : on se contentera de ne pas se reconnaître dans la liste */
  }
}

/**
 * Relit l'état : ce que dit l'API, et ce que dit le navigateur.
 *
 * LES DEUX PEUVENT DIVERGER, et c'est le navigateur qui a raison sur
 * « cet appareil est-il abonné ? » : effacer les données du site supprime
 * l'abonnement local sans que le serveur en sache rien.
 */
export async function rafraichirPush(): Promise<void> {
  if (!supporte()) return;
  permission.value = Notification.permission;
  try {
    config.value = await api.get<PushConfig>('/me/push');
  } catch {
    config.value = { available: false, publicKey: null, devices: [] };
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  abonneIci.value = !!(await reg?.pushManager.getSubscription());
  // Abonné localement mais inconnu du serveur : l'identifiant mémorisé ne sert
  // plus à rien, on l'oublie plutôt que de le montrer comme « cet appareil ».
  const id = lireId();
  if (id && !appareils.value.some((a) => a.id === id)) ecrireId(null);
}

/**
 * Active le push sur CET appareil.
 *
 * Rend `false` si ça n'a pas abouti — permission refusée, ou navigateur qui
 * s'y oppose. L'appelant affiche alors l'état, qui a déjà été mis à jour.
 */
export async function activerPush(): Promise<boolean> {
  if (!supporte() || !config.value?.publicKey) return false;
  occupe.value = true;
  try {
    /*
     * LA DEMANDE DE PERMISSION DOIT SUIVRE UN CLIC. Les navigateurs refusent
     * (et retiennent contre nous) une demande spontanée au chargement : c'est
     * pourquoi rien ici ne part tout seul, jamais.
     */
    permission.value = await Notification.requestPermission();
    if (permission.value !== 'granted') return false;

    const reg = await navigator.serviceWorker.ready;
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        // Obligatoire : on s'engage à AFFICHER quelque chose à chaque push.
        // C'est ce qui interdit d'utiliser ce canal pour pister quelqu'un.
        userVisibleOnly: true,
        applicationServerKey: cleEnOctets(config.value.publicKey),
      }));

    const j = sub.toJSON();
    const { device } = await api.post<{ device: PushDevice }>('/me/push/subscriptions', {
      endpoint: j.endpoint,
      p256dh: j.keys?.p256dh,
      auth: j.keys?.auth,
      appareil: nommerAppareil(),
    });
    ecrireId(device.id);
    abonneIci.value = true;
    await rafraichirPush();
    return true;
  } catch {
    return false;
  } finally {
    occupe.value = false;
  }
}

/**
 * Coupe le push sur cet appareil.
 *
 * ON DÉFAIT DANS LES DEUX SENS — le navigateur ET le serveur. Ne défaire que
 * le navigateur laisserait une ligne morte en base, à qui l'on écrirait
 * jusqu'au premier refus ; ne défaire que le serveur laisserait un abonnement
 * local qu'aucun écran ne montre plus.
 */
export async function desactiverPush(): Promise<void> {
  if (!supporte()) return;
  occupe.value = true;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.del('/me/push/subscriptions', { endpoint: sub.endpoint }).catch(() => {
        /* le serveur nettoiera au premier envoi refusé */
      });
      await sub.unsubscribe();
    }
    ecrireId(null);
    abonneIci.value = false;
    await rafraichirPush();
  } finally {
    occupe.value = false;
  }
}

/**
 * À LA DÉCONNEXION, on coupe.
 *
 * Sans cela, le collègue qui se connecte ensuite sur le même poste verrait
 * s'afficher les notifications du précédent sur son écran de veille. Côté
 * serveur, l'unicité de l'endpoint rattrape déjà le cas — mais seulement si le
 * suivant active le push à son tour, ce que rien ne garantit.
 *
 * Ce n'est PAS une gêne pour l'usage normal : fermer l'outil n'est pas se
 * déconnecter, et c'est bien tout l'intérêt du push.
 */
export async function couperPushALaDeconnexion(): Promise<void> {
  try {
    await desactiverPush();
  } catch {
    /* on se déconnecte quand même */
  }
}

/**
 * Branche le clic sur une notification système.
 *
 * Le service worker ne sait pas naviguer dans une application à page unique :
 * il reprend la fenêtre et nous passe le chemin, qu'on ouvre avec le routeur.
 * Le faire naviguer lui-même rechargerait tout l'écran — donc perdrait une
 * saisie en cours pour aller voir une notification.
 */
export function surClicNotification(aller: (lien: string) => void): void {
  if (!supporte()) return;
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const d = e.data as { type?: string; lien?: string } | null;
    if (d?.type === 'push-navigation' && d.lien) aller(d.lien);
  });
}

/**
 * La clé VAPID, du base64url au tableau d'octets attendu par `subscribe`.
 *
 * `atob` ne connaît que le base64 ordinaire : sans la substitution des deux
 * caractères et le remplissage, la clé est rejetée — et le message d'erreur du
 * navigateur ne dit pas pourquoi.
 */
function cleEnOctets(base64url: string): Uint8Array<ArrayBuffer> {
  const bourrage = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + bourrage).replace(/-/g, '+').replace(/_/g, '/');
  const brut = atob(base64);
  // Le tampon est créé explicitement : `new Uint8Array(n)` se type sur
  // `ArrayBufferLike`, que `applicationServerKey` refuse (il pourrait s'agir
  // d'une mémoire partagée, qu'on ne peut pas transférer).
  const octets = new Uint8Array(new ArrayBuffer(brut.length));
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
}

/**
 * « Windows · Chrome » — de quoi reconnaître ses appareils dans la liste.
 *
 * Grossier À DESSEIN. On ne cherche pas à identifier une machine, seulement à
 * aider quelqu'un à distinguer son poste de son téléphone ; et l'agent
 * utilisateur ment assez pour qu'une analyse fine n'apporte qu'une fausse
 * précision. C'est composé ICI et envoyé tel quel : l'API n'interprète rien.
 */
function nommerAppareil(): string {
  const ua = navigator.userAgent;
  const systeme =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Macintosh/.test(ua) ? 'Mac'
    : /Linux/.test(ua) ? 'Linux'
    : 'Appareil';
  const navigateur =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Navigateur';
  return `${systeme} · ${navigateur}`;
}
